/**
 * TrainingSuite — premium training & certification proposition for the
 * public homepage. Sits between the FeaturesMoment (trust) and Pricing
 * (SaaS) blocks so the visitor sees, in order:
 *
 *   Features → Training products → SaaS pricing → Bottom CTA
 *
 * The section covers three commercial audiences (individual, team, bespoke),
 * three course-package sizes (6/12/16), the material-to-record journey for
 * bespoke work, and the CanniComply certification mark.
 *
 * There is deliberately no pricing, no payment flow and no CRM integration.
 * Every CTA lands on the new /enquire questionnaire.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  Users,
  Sparkles,
  ArrowRight,
  BookOpen,
  ClipboardCheck,
  Award,
  Archive,
  FileText,
  ShieldCheck,
  Building2,
  UserCheck,
  BadgeCheck,
  Plus,
  Minus,
  Check,
} from "lucide-react";
import Button from "@/components/site/Button";

/* ================================================================== *
 *  1 · Audience cards — one thing per audience, high scent
 * ================================================================== */
const AUDIENCES = [
  {
    tag: "For individuals",
    icon: UserCheck,
    testid: "training-audience-individual",
    tint: "emerald",
    // Prefills Q01 (Audience) on /enquire so the visitor does not have to
    // repeat the question they've already answered by clicking this card.
    intent: "individual",
    cta: "Enquire as an individual",
    title: "Take a course. Earn a certificate.",
    body:
      "Study online, complete the verified assessment and receive a signed CanniComply certificate you can add to your professional record.",
    bullets: [
      "Structured, self-paced modules",
      "Verified competency assessment",
      "Signed, shareable certificate",
    ],
  },
  {
    tag: "For teams & businesses",
    icon: Building2,
    testid: "training-audience-business",
    tint: "gold",
    intent: "team",
    cta: "Enquire for my team",
    title: "Certify every member of staff.",
    body:
      "Roll out a training package to your whole workforce, track completion per person and hold a permanent, inspection-grade record for each.",
    bullets: [
      "Bulk enrolment across sites",
      "Per-employee evidence pack",
      "Owner + manager oversight",
    ],
  },
  {
    tag: "Bespoke",
    icon: Sparkles,
    testid: "training-audience-bespoke",
    tint: "gold-strong",
    intent: "bespoke",
    cta: "Enquire about bespoke",
    title: "Your material. Turned into training.",
    body:
      "Hand us your SOPs, policies and internal documents. We build them into structured courses, assessments and certificates for your team.",
    bullets: [
      "Your SOPs become modules",
      "Custom competency assessments",
      "Certificates in your language",
    ],
  },
];

/* ================================================================== *
 *  2 · Course packages — three sizes, no pricing
 * ================================================================== */
const PACKAGES = [
  {
    slug: "essentials",
    size: 6,
    label: "Essentials",
    title: "The Essentials",
    moduleLabel: "06 MODULES",
    subtitle: "Core competencies for one team member.",
    testid: "package-row-essentials",
    features: [
      "Six curated foundation modules",
      "Assessment after each module",
      "One signed certificate per pass",
    ],
    featured: false,
  },
  {
    slug: "professional",
    size: 12,
    label: "Professional",
    title: "The Professional",
    moduleLabel: "12 MODULES",
    subtitle: "A rounded programme for regulated roles.",
    testid: "package-row-professional",
    features: [
      "Twelve modules across policy, safety, quality",
      "Retention checks + refresher prompts",
      "CanniComply certification mark",
    ],
    featured: true,
  },
  {
    slug: "advanced",
    size: 16,
    label: "Advanced",
    title: "The Advanced",
    moduleLabel: "16 MODULES",
    subtitle: "The full stack for department leads.",
    testid: "package-row-advanced",
    features: [
      "Sixteen modules incl. governance & audit",
      "Manager-grade competency review",
      "Priority certification issuance",
    ],
    featured: false,
  },
];

/* ================================================================== *
 *  3 · Material → Record journey
 * ================================================================== */
const JOURNEY = [
  { label: "Material",    icon: FileText,        note: "Your SOPs & policies" },
  { label: "Course",      icon: BookOpen,        note: "Structured learning" },
  { label: "Assessment",  icon: ClipboardCheck,  note: "Verified competency" },
  { label: "Certificate", icon: Award,           note: "Signed & shareable" },
  { label: "Record",      icon: Archive,         note: "Retained · immutable" },
];

/* ================================================================== *
 *  Component
 * ================================================================== */
export default function TrainingSuite() {
  return (
    <section
      id="training"
      data-testid="training-suite-section"
      className="relative py-20 md:py-28 border-t border-white/[0.04] overflow-hidden"
    >
      {/* Ambient glow — consistent with FeaturesMoment / BottomCTA */}
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-30" />
      <div className="absolute -top-24 right-[-10%] pointer-events-none h-[420px] w-[420px] rounded-full bg-[#D4A65A]/[0.05] blur-3xl" />
      <div className="absolute -bottom-24 left-[-10%] pointer-events-none h-[420px] w-[420px] rounded-full bg-emerald-500/[0.045] blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* ─── Section header ─── */}
        <div className="max-w-3xl">
          <div className="eyebrow inline-flex items-center gap-2">
            <GraduationCap size={12} className="text-[#D4A65A]" />
            Training &amp; Certification
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.65 }}
            className="mt-4 font-display text-4xl md:text-[54px] tracking-[-0.035em] text-white leading-[1.02] font-semibold"
          >
            Certified competency,{" "}
            <span className="text-[#D4A65A]">built around you.</span>
          </motion.h2>
          <p className="mt-5 text-slate-400 text-[15px] md:text-lg leading-relaxed max-w-2xl">
            Buy a course. Train a whole team. Or hand us your SOPs and we&apos;ll
            turn them into a proper training programme with assessments,
            certificates and a permanent record.
          </p>
        </div>

        {/* ─── 1 · Audience cards ─── */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            const isPrimary = a.tint === "gold-strong";
            const isGold = a.tint === "gold" || a.tint === "gold-strong";
            return (
              <motion.a
                key={a.tag}
                href={`/enquire?intent=${a.intent}`}
                data-testid={a.testid}
                aria-label={a.cta}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-xl p-6 md:p-7 flex flex-col overflow-hidden transition-colors duration-300 ${
                  isPrimary
                    ? "bg-gradient-to-b from-[#12172A] to-[#080F1F] border border-[#D4A65A]/40 hover:border-[#D4A65A]/70 shadow-[0_30px_80px_-25px_rgba(212,166,90,0.28)]"
                    : "bg-[#070d1c] border border-white/[0.06] hover:border-[#D4A65A]/40"
                }`}
              >
                {isPrimary && (
                  <span className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#D4A65A]/15 blur-3xl" />
                )}
                <div className="relative flex items-center gap-2">
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-md shrink-0 ${
                      isGold
                        ? "bg-[#D4A65A]/12 border border-[#D4A65A]/30 text-[#D4A65A]"
                        : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                    }`}
                  >
                    <Icon size={15} />
                  </span>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-slate-400">
                    {a.tag}
                  </div>
                </div>
                <h3 className="relative mt-5 font-display text-[22px] md:text-[24px] tracking-tight text-white font-semibold leading-[1.15]">
                  {a.title}
                </h3>
                <p className="relative mt-3 text-[13.5px] text-slate-400 leading-relaxed">
                  {a.body}
                </p>
                <ul className="relative mt-5 space-y-2.5 flex-1">
                  {a.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[13px] text-slate-300 leading-snug"
                    >
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full shrink-0 ${
                          isGold
                            ? "bg-[#D4A65A]/12 border border-[#D4A65A]/30"
                            : "bg-emerald-500/10 border border-emerald-500/30"
                        }`}
                      >
                        <BadgeCheck
                          size={9}
                          className={isGold ? "text-[#D4A65A]" : "text-emerald-300"}
                        />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div
                  className={`relative mt-6 inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] font-semibold transition-colors ${
                    isGold
                      ? "text-[#D4A65A] group-hover:text-[#F0D497]"
                      : "text-emerald-300 group-hover:text-emerald-200"
                  }`}
                  data-testid={`${a.testid}-cta`}
                >
                  {a.cta}
                  <ArrowRight
                    size={12}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </div>
              </motion.a>
            );
          })}
        </div>

        {/* ─── 2 · Course packages — "The Curator's Ledger" ─── */}
        <PackageLedger />


        {/* ─── 3 · Bespoke journey — material to record ─── */}
        <div className="mt-24 md:mt-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow">Bespoke</div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-[-0.03em] text-white leading-[1.05] font-semibold">
                Training built{" "}
                <span className="text-[#D4A65A]">for your business.</span>
              </h3>
              <p className="mt-5 text-[14.5px] text-slate-400 leading-relaxed max-w-md">
                Your SOPs. Your procedures. Your training material.
                CanniComply turns what you already use into structured online
                learning, verified assessments and a permanent training record
                for every member of staff.
              </p>
              <ul className="mt-7 space-y-3 max-w-md">
                {[
                  ["Your material", "Send us your SOPs, policies and manuals."],
                  ["Our system", "We build the course, questions and grading."],
                  ["Your team", "Staff learn, sit the assessment and pass."],
                  ["Your record", "Signed certificates and an immutable log."],
                ].map(([k, v]) => (
                  <li key={k} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#D4A65A]/12 border border-[#D4A65A]/30 shrink-0">
                      <BadgeCheck size={10} className="text-[#D4A65A]" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-white font-semibold tracking-tight">
                        {k}
                      </div>
                      <div className="text-[12.5px] text-slate-400 leading-snug">
                        {v}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button
                  as="a"
                  href="/enquire?intent=bespoke"
                  data-testid="training-bespoke-cta"
                  variant="primary"
                  size="md"
                >
                  Discuss a bespoke programme
                  <ArrowRight size={14} />
                </Button>
              </div>
            </div>

            {/* Visual pipeline */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.65 }}
              className="lg:col-span-7 relative rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] p-5 md:p-8"
              data-testid="training-journey"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 h-52 w-52 rounded-full bg-[#D4A65A]/[0.1] blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full bg-emerald-500/[0.08] blur-3xl" />

              <div className="relative flex items-center justify-between mb-6">
                <div className="eyebrow inline-flex items-center gap-2">
                  <span className="h-1 w-3 bg-[#D4A65A]/80" />
                  Your material → their record
                </div>
                <span className="text-[9.5px] font-mono text-slate-500 hidden sm:inline">
                  cannicomply / pipeline
                </span>
              </div>

              {/* Journey rail — horizontal on md+, vertical on mobile */}
              <ol className="relative flex md:flex-row flex-col md:items-stretch gap-3 md:gap-2">
                {JOURNEY.map((step, idx) => {
                  const Icon = step.icon;
                  const isLast = idx === JOURNEY.length - 1;
                  return (
                    <li
                      key={step.label}
                      data-testid={`training-journey-step-${idx}`}
                      className="relative flex-1 min-w-0"
                    >
                      <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] backdrop-blur px-3 py-4 md:py-5 flex md:flex-col items-center gap-3 md:gap-2 text-center md:text-center hover:border-[#D4A65A]/40 transition-colors">
                        <span className="h-9 w-9 rounded-lg bg-[#D4A65A]/12 border border-[#D4A65A]/25 flex items-center justify-center shrink-0">
                          <Icon size={15} className="text-[#D4A65A]" />
                        </span>
                        <div className="min-w-0 md:mt-1">
                          <div className="text-white text-[12.5px] font-semibold tracking-tight">
                            {step.label}
                          </div>
                          <div className="text-[10.5px] text-slate-400 leading-snug mt-0.5">
                            {step.note}
                          </div>
                        </div>
                      </div>
                      {/* Connector arrows — desktop only, drawn between cards */}
                      {!isLast && (
                        <span
                          aria-hidden="true"
                          className="hidden md:block absolute top-1/2 -right-1.5 -translate-y-1/2 text-[#D4A65A]/40 z-10"
                        >
                          <ArrowRight size={14} />
                        </span>
                      )}
                      {/* Vertical rail dot — mobile only */}
                      {!isLast && (
                        <span
                          aria-hidden="true"
                          className="md:hidden absolute -bottom-2 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-[#D4A65A]/50"
                        />
                      )}
                    </li>
                  );
                })}
              </ol>

              <div className="relative mt-6 pt-5 border-t border-white/[0.06] flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-slate-500 uppercase tracking-[0.22em] font-semibold">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={11} className="text-emerald-400" />
                  SHA-256 signed
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[#D4A65A]" />
                  Retained 7 years
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-emerald-400" />
                  Auditor-ready
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ─── 4 · Certification mark ─── */}
        <div className="mt-24 md:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#050B18]"
            data-testid="training-certification-mark"
          >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0B1424] via-[#050B18] to-[#080F1F]" />
            <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#D4A65A]/[0.12] blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/[0.08] blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 p-6 md:p-10 items-center">
              <div className="lg:col-span-7">
                <div className="eyebrow inline-flex items-center gap-2">
                  <Award size={12} className="text-[#D4A65A]" />
                  Certification mark
                </div>
                <h3 className="mt-3 font-display text-3xl md:text-[42px] tracking-[-0.03em] text-white leading-[1.04] font-semibold">
                  The CanniComply mark.{" "}
                  <span className="text-[#D4A65A]">Proof, on your terms.</span>
                </h3>
                <p className="mt-4 text-[14.5px] text-slate-400 leading-relaxed max-w-xl">
                  Every certified team member and every certified business
                  earns the right to display the CanniComply mark — on
                  websites, tenders, on-site signage and staff records. A
                  quiet, credible signal that your training is verifiable, not
                  claimed.
                </p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl">
                  {[
                    "Public verify URL per certificate",
                    "Immutable SHA-256 signature chain",
                    "Renewable per module or per staff",
                    "Usable on your marketing & tender packs",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2.5 text-[13px] text-slate-300 leading-snug"
                    >
                      <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#D4A65A]/12 border border-[#D4A65A]/30 shrink-0">
                        <BadgeCheck size={9} className="text-[#D4A65A]" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    as="a"
                    href="/enquire?intent=certification"
                    data-testid="training-certification-cta"
                    variant="primary"
                    size="md"
                  >
                    Certify your team
                    <ArrowRight size={14} />
                  </Button>
                  <Button
                    as="a"
                    href="/enquire?intent=individual"
                    data-testid="training-individual-cta"
                    variant="ghost"
                    size="md"
                  >
                    <Users size={13} className="text-[#D4A65A]" />
                    I&apos;m an individual
                  </Button>
                </div>
              </div>

              {/* Mark preview — CanniComply "Verified" trust badge.
                  Modelled on TrustPilot/BSI-style verified-merchant marks
                  so it can sit cleanly on a client's own website. Paper-
                  white card, deep green check + rule, dark serif wordmark.
                  Everything else in this section (headline, bullets,
                  CTAs) is untouched. */}
              <div className="lg:col-span-5">
                <div className="relative mx-auto w-full max-w-sm">
                  {/* Soft gold ambient glow behind the badge — keeps it
                      anchored to the section palette without touching
                      the mark itself. */}
                  <div className="absolute inset-0 rounded-[28px] bg-[#D4A65A]/12 blur-3xl" />

                  {/* Dark navy card — restyled to match the PNG reference */}
                  <div className="relative rounded-2xl bg-[#0B1220] p-8 md:p-10 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.85),0_1px_0_0_rgba(255,255,255,0.04)_inset] ring-1 ring-white/10">
                    {/* Inner hairline frame — matches the PNG's inset border */}
                    <div className="pointer-events-none absolute inset-3 rounded-xl ring-1 ring-white/[0.06]" />

                    {/* Emerald check circle with soft halo */}
                    <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full border-[1.5px] border-emerald-400/50 bg-emerald-500/10 shadow-[0_0_28px_-4px_rgba(52,211,153,0.45)]">
                      <Check
                        size={30}
                        strokeWidth={3}
                        className="text-emerald-400"
                      />
                    </div>

                    {/* Wordmark — mixed-case, white "Canni" + gold "Comply" */}
                    <div className="mt-6 text-center font-display font-bold text-[28px] md:text-[32px] tracking-[-0.005em] leading-none">
                      <span className="text-white">Canni</span><span className="text-[#D4A65A]">Comply</span>
                    </div>

                    {/* VERIFIED with gold side rules */}
                    <div className="mt-4 flex items-center justify-center gap-3">
                      <span className="h-px w-12 bg-[#D4A65A]/60" />
                      <span className="text-[#D4A65A] text-[13px] tracking-[0.44em] font-semibold">
                        VERIFIED
                      </span>
                      <span className="h-px w-12 bg-[#D4A65A]/60" />
                    </div>

                    {/* Divider */}
                    <div className="mt-6 h-px w-full bg-white/10" />

                    {/* Description */}
                    <div className="mt-5 text-center text-white/70 text-[14px] leading-[1.55]">
                      Compliance &amp; Training
                      <br />
                      Managed by{" "}
                      <span className="font-semibold text-[#D4A65A]">CanniComply</span>
                    </div>

                    {/* Gold base rule — shortened + centred */}
                    <div className="mt-7 mx-auto h-[3px] w-24 rounded-full bg-[#D4A65A]" />

                    {/* Meta caption — inside the card, matches the PNG reference */}
                    <div className="mt-6 text-center text-[10.5px] uppercase tracking-[0.28em] text-white/40 font-mono">
                      Training &middot; Assessment &middot; Record
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


/* ================================================================== *
 *  PackageLedger — "The Curator's Ledger"
 *  --------------------------------------------------------------
 *  A high-end, asymmetrical two-column composition for the three
 *  course packages. Deliberately breaks the surrounding card-grid
 *  rhythm: a sticky editorial column on the left, a hair-line
 *  ledger of expandable rows on the right, ONE dominant CTA.
 *  Professional expands by default; hover on a row dims siblings
 *  (spotlight effect) and slides the title in.
 * ================================================================== */
function PackageLedger() {
  const [openId, setOpenId] = useState("professional");
  const [hoverId, setHoverId] = useState(null);

  return (
    <div
      data-testid="course-packages-section"
      className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center"
    >
      {/* ── Left column — editorial context, vertically centred so it
          doesn't leave a black void beside the taller ledger ── */}
      <div className="lg:col-span-5">
        <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[#D4A65A] font-semibold">
          <span className="inline-block h-px w-8 align-middle bg-[#D4A65A]/60 mr-3" />
          Course packages
        </div>
        <h3 className="mt-6 font-display text-4xl md:text-5xl lg:text-[64px] tracking-[-0.02em] text-white leading-[1.02] font-semibold">
          Curated for{" "}
          <span className="text-[#D4A65A]">compliance.</span>
        </h3>
        <p className="mt-6 text-[15px] md:text-base text-white/70 leading-relaxed max-w-md">
          Three bespoke bundles for individuals and teams. Enquire to
          configure the modules that match your precise site
          requirements &mdash; role, department, jurisdiction.
        </p>

        <div className="mt-8 flex flex-col items-start gap-4">
          <Button
            as="a"
            href="/enquire?intent=packages"
            data-testid="packages-master-enquire-button"
            variant="primary"
            size="lg"
          >
            Configure your package
            <ArrowRight size={14} />
          </Button>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.26em] text-white/40">
            No payment &middot; Human reply &middot; &lt; 4 hours
          </div>
        </div>

        {/* Editorial mark — quiet meta strip */}
        <div className="mt-12 hidden lg:flex items-center gap-3 text-[10px] font-mono tracking-[0.3em] text-white/25 uppercase">
          <span className="h-px flex-1 bg-[#D4A65A]/25" />
          Three programmes
          <span className="h-px flex-1 bg-[#D4A65A]/25" />
        </div>
      </div>

      {/* ── Right column — accordion ledger of packages ── */}
      <ol
        className="lg:col-span-7 relative border-t border-b border-[#D4A65A]/25"
        onMouseLeave={() => setHoverId(null)}
      >
        {PACKAGES.map((p) => {
          const isOpen = openId === p.slug;
          const isDimmed = hoverId !== null && hoverId !== p.slug;
          return (
            <li
              key={p.slug}
              data-testid={p.testid}
              className="border-b border-[#D4A65A]/25 last:border-b-0"
            >
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={`package-panel-${p.slug}`}
                onClick={() => setOpenId(isOpen ? "" : p.slug)}
                onMouseEnter={() => setHoverId(p.slug)}
                className="group relative w-full text-left py-8 md:py-10 flex items-start justify-between gap-6 cursor-pointer"
                style={{
                  opacity: isDimmed ? 0.35 : 1,
                  transition: "opacity 350ms ease",
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono tracking-[0.28em] text-[#D4A65A] uppercase">
                    <span>[ {p.moduleLabel} ]</span>
                    {p.featured && (
                      <span
                        className="inline-flex items-center rounded-full bg-[#0F5D3F] px-2.5 py-1 text-[9px] tracking-[0.24em] text-white font-mono"
                        data-testid="package-row-professional-tag"
                      >
                        Most popular
                      </span>
                    )}
                  </div>
                  <h4 className="mt-4 font-display text-3xl md:text-[38px] lg:text-[44px] tracking-[-0.015em] text-white font-semibold leading-[1.05]">
                    <span className="inline-block transition-transform duration-500 ease-out group-hover:translate-x-2">
                      {p.title}
                    </span>
                  </h4>
                  <p className="mt-3 text-[14px] md:text-[15px] text-white/60 max-w-lg leading-relaxed">
                    {p.subtitle}
                  </p>
                </div>
                <div
                  aria-hidden="true"
                  className={`shrink-0 mt-2 h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
                    isOpen
                      ? "bg-[#D4A65A]/15 border border-[#D4A65A]/60 text-[#D4A65A]"
                      : "border border-[#D4A65A]/30 text-[#D4A65A]/80 group-hover:bg-[#D4A65A]/10 group-hover:border-[#D4A65A]/60"
                  }`}
                >
                  {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                </div>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="panel"
                    id={`package-panel-${p.slug}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] },
                      opacity: { duration: 0.35, delay: isOpen ? 0.12 : 0 },
                    }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="pb-10 pr-2 md:pr-14 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-6">
                      {p.features.map((f, i) => (
                        <div
                          key={f}
                          className="border-t border-[#D4A65A]/25 pt-4"
                          data-testid={`${p.testid}-feature-${i}`}
                        >
                          <div className="text-[10px] font-mono tracking-[0.28em] text-[#D4A65A]/80 uppercase">
                            {String(i + 1).padStart(2, "0")}
                          </div>
                          <div className="mt-2 text-[13.5px] text-white/85 leading-snug font-medium">
                            {f}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

